<template lang="pug">
svg.panda-mascot(viewBox="-3 -4 77 72" aria-hidden="true" @mouseenter="react")
  g(ref="bodyRef" class="panda-mascot__body")
    //- PandaFan logo: head silhouette with the S-shaped face cut out
    path(fill="currentColor" fill-rule="evenodd" d="M35.3802036,1.42108547e-14 C41.928325,1.42108547e-14 47.8396702,1.90260854 52.872573,5.11459741 C54.4943258,3.09031457 56.9663173,1.79725072 59.7355593,1.79725072 C64.6202966,1.79725072 68.5801586,5.82053351 68.5801586,10.7835043 C68.5801586,13.7248423 67.1892888,16.3361224 65.0396692,17.9741665 C67.5482466,22.2681706 69.3021696,27.0473725 70.1676507,31.9818462 C73.1321338,48.8836166 64.9004317,64.2401926 35.242823,64.2401926 C5.5852143,64.2401926 -1.71916254,47.5422548 0.317995252,32.1200963 C0.937739769,27.4283644 2.7195056,22.6185592 5.41754749,18.1972617 C2.90118191,16.6130127 1.22513363,13.781916 1.22513363,10.5530876 C1.22513363,5.59011676 5.18499559,1.56683397 10.0697329,1.56683397 C13.1538764,1.56683397 15.8693227,3.17069711 17.450539,5.6018887 C22.6673026,2.14269573 28.7774088,2.84217094e-14 35.3802036,1.42108547e-14 Z M35.3641791,4.73825654 C33.3761698,4.73825654 31.4323761,4.94308801 29.5560219,5.33183919 C23.6440398,7.22818638 19.3672663,12.8228652 19.3672663,19.4365059 C19.3672663,27.6050864 26.3772468,33.3299575 33.9886609,34.2398987 C41.600075,35.1498399 48.4670833,39.3647345 48.4670833,47.5437709 C48.4670833,54.6618919 43.3503923,58.3198927 36.7403842,59.7382565 C58.667157,59.7382565 68.5175278,48.4097693 65.9759761,33.2202719 C63.4344244,18.0307745 50.5231919,4.73825654 35.3641791,4.73825654 Z")
    g(ref="eyesRef" class="panda-mascot__eyes")
      rect(x="28.7" y="13.5" width="8.1" height="17" rx="4.05" fill="currentColor")
      rect(x="45.2" y="13.5" width="8.1" height="17" rx="4.05" fill="currentColor")
</template>

<script lang="ts" setup>
// Idle mascot: plays a random micro-expression every few seconds and one on hover.
const props = withDefaults(defineProps<{ minDelay?: number, maxDelay?: number }>(), {
  minDelay: 2500,
  maxDelay: 6000,
})

const bodyRef = ref<SVGGElement | null>(null)
const eyesRef = ref<SVGGElement | null>(null)
const spring = 'cubic-bezier(.34,1.56,.64,1)'
const pick = <T,>(items: T[]): T => items[Math.floor(Math.random() * items.length)]!
const rand = (a: number, b: number) => a + Math.random() * (b - a)

const blink = () => eyesRef.value?.animate(
  [{ transform: 'scaleY(1)' }, { transform: 'scaleY(.1)', offset: 0.45 }, { transform: 'scaleY(1)' }],
  { duration: 170, easing: 'ease-in-out' },
)

const actions: { weight: number, run: () => void }[] = [
  { weight: 4, run: blink },
  { weight: 1, run: () => { const a = blink(); if (a) a.onfinish = () => { setTimeout(blink, 90) } } },
  { weight: 3, run: () => { // glance sideways, hold, come back
    const to = `translate(${pick([-3.5, 3.5])}px, ${pick([0, -1, 1.2])}px)`
    eyesRef.value?.animate(
      [{ transform: 'translate(0, 0)' }, { transform: to, offset: 0.15 }, { transform: to, offset: 0.8 }, { transform: 'translate(0, 0)' }],
      { duration: rand(900, 1400), easing: 'ease-in-out' },
    )
  } },
  { weight: 1, run: () => { // happy squint
    eyesRef.value?.animate(
      [{ transform: 'scaleY(1)' }, { transform: 'scale(1.1, .4)', offset: 0.2, easing: 'ease-in-out' }, { transform: 'scale(1.1, .4)', offset: 0.75 }, { transform: 'scaleY(1)' }],
      { duration: 900, easing: spring },
    )
  } },
  { weight: 2, run: () => { // curious head tilt
    const deg = pick([-8, 8])
    bodyRef.value?.animate(
      [{ transform: 'rotate(0)' }, { transform: `rotate(${deg}deg)`, offset: 0.25, easing: 'ease-in-out' }, { transform: `rotate(${deg}deg)`, offset: 0.75, easing: spring }, { transform: 'rotate(0)' }],
      { duration: rand(1100, 1500) },
    )
  } },
  { weight: 2, run: () => { // squish and bounce back
    bodyRef.value?.animate(
      [{ transform: 'scale(1, 1)' }, { transform: 'scale(1.12, .86)', offset: 0.3, easing: spring }, { transform: 'scale(.96, 1.06)', offset: 0.6 }, { transform: 'scale(1, 1)' }],
      { duration: 520, easing: 'ease-out' },
    )
  } },
]
const totalWeight = actions.reduce((sum, a) => sum + a.weight, 0)

let reducedMotion = false
const playRandom = () => {
  if (reducedMotion) return blink()
  let r = Math.random() * totalWeight
  for (const action of actions) {
    r -= action.weight
    if (r < 0) return action.run()
  }
}

let lastReact = 0
const react = () => {
  if (Date.now() - lastReact < 1200) return
  lastReact = Date.now()
  playRandom()
}

let timer: ReturnType<typeof setTimeout> | undefined
const schedule = () => {
  timer = setTimeout(() => {
    if (!document.hidden) playRandom()
    schedule()
  }, rand(props.minDelay, props.maxDelay))
}

onMounted(() => {
  if (!bodyRef.value?.animate) return
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  schedule()
})
onBeforeUnmount(() => clearTimeout(timer))
</script>

<style scoped>
.panda-mascot {
  width: 1em;
  height: 1em;
  overflow: visible;
}
.panda-mascot__body,
.panda-mascot__eyes {
  transform-box: fill-box;
}
.panda-mascot__body {
  transform-origin: 50% 100%;
}
.panda-mascot__eyes {
  transform-origin: 50% 50%;
}
</style>
